import { compareStory } from './storyTime'
import type {
  BoardVersion,
  ExerciseRun,
  RoomChatPhase,
  ScenarioComment,
  ScenarioSession,
} from './types'
import { commentsVisibleAt } from './resolveSession'

function versionById(
  versions: readonly BoardVersion[] | undefined,
  versionId: string | null | undefined,
): BoardVersion | null {
  if (!versionId || !versions?.length) return null
  return versions.find((v) => v.id === versionId) ?? null
}

/** Script lines authored on the version seed (fallback: session.comments for canned templates). */
export function scriptCommentsForVersion(
  session: ScenarioSession,
  versionId: string | null | undefined,
): ScenarioComment[] {
  const ver = versionById(session.boardVersions, versionId)
  const fromSeed = ver?.seed.comments
  if (Array.isArray(fromSeed) && fromSeed.length) {
    return fromSeed.filter((c) => c && c.source === 'script' && !c.removedAtWallIso)
  }
  return (session.comments ?? []).filter((c) => c.source === 'script' && !c.removedAtWallIso)
}

/**
 * Script-only seed for a new run row (legacy `exerciseRuns[].comments`).
 * Live/user chat lives on `session.roomChat` — not carried between runs.
 */
export function buildChatForNewRun(
  session: ScenarioSession,
  newRun: Pick<ExerciseRun, 'id' | 'versionId' | 'number'>,
  _priorRuns?: readonly ExerciseRun[] | null,
): ScenarioComment[] {
  return scriptCommentsForVersion(session, newRun.versionId).map((c) => ({
    ...structuredClone(c),
    source: 'script' as const,
  }))
}

/** @deprecated Prefer roomChat — kept for heal / older callers. */
export function resolveActiveChatRun(
  session: ScenarioSession,
  opts?: { watchingRunId?: string | null },
): ExerciseRun | null {
  const vid = session.selectedVersionId
  const runs = (session.exerciseRuns ?? []).filter((r) => !vid || r.versionId === vid)
  if (!runs.length) return null
  if (opts?.watchingRunId) {
    const watched = runs.find((r) => r.id === opts.watchingRunId)
    if (watched) return watched
  }
  const inProgress = [...runs].reverse().find((r) => r.status === 'in_progress')
  if (inProgress) return inProgress
  return [...runs].sort((a, b) => b.number - a.number)[0] ?? null
}

export function patchRunComments(
  session: ScenarioSession,
  runId: string,
  comments: readonly ScenarioComment[],
): ScenarioSession {
  const runs = session.exerciseRuns ?? []
  if (!runs.some((r) => r.id === runId)) return session
  return {
    ...session,
    exerciseRuns: runs.map((r) => (r.id === runId ? { ...r, comments: [...comments] } : r)),
  }
}

export function patchRoomChat(
  session: ScenarioSession,
  comments: readonly ScenarioComment[],
): ScenarioSession {
  return { ...session, roomChat: [...comments] }
}

/**
 * Union live room threads by id. Incoming wins on the same id; new ids append.
 * Empty incoming keeps `local` — pass `replaceIfEmpty` for an explicit clear.
 */
export function mergeRoomChat(
  local: readonly ScenarioComment[] | undefined,
  incoming: readonly ScenarioComment[] | undefined,
  opts?: { replaceIfEmpty?: boolean },
): ScenarioComment[] {
  const prev = local ?? []
  const next = incoming ?? []
  if (next.length === 0) return opts?.replaceIfEmpty ? [] : [...prev]
  const byId = new Map<string, ScenarioComment>()
  const order: string[] = []
  for (const c of prev) {
    if (!c?.id) continue
    byId.set(c.id, c)
    order.push(c.id)
  }
  for (const c of next) {
    if (!c?.id) continue
    if (!byId.has(c.id)) order.push(c.id)
    byId.set(c.id, c)
  }
  return order.map((id) => byId.get(id)!).filter(Boolean)
}

/** Script-only slice for version seeds (never persist live lines onto the plan). */
export function scriptCommentsForSeed(session: ScenarioSession): ScenarioComment[] {
  const fromSession = (session.comments ?? []).filter((c) => c.source === 'script')
  if (fromSession.length) return structuredClone(fromSession)
  const ver = versionById(session.boardVersions, session.selectedVersionId)
  const fromSeed = ver?.seed.comments?.filter((c) => c.source === 'script') ?? []
  return structuredClone(fromSeed)
}

/**
 * Device-local unread watermark (wall ISO of last-seen live room line).
 * Scoped by reader so two accounts on the same origin (A/P tabs) do not share a tip.
 */
export function chatReadStorageKey(
  instanceId: string,
  readerId?: string | null,
): string {
  const who = (readerId || '').trim() || 'anon'
  return `buxus.sc.chatRead:${instanceId}:${who}`
}

export function readChatWatermark(
  instanceId: string | null | undefined,
  readerId?: string | null,
): string | null {
  if (!instanceId) return null
  try {
    const keyed = localStorage.getItem(chatReadStorageKey(instanceId, readerId))
    if (keyed) return keyed
    // Migrate pre-reader key once (same-tab A/P used to clobber each other).
    const legacy = localStorage.getItem(`buxus.sc.chatRead:${instanceId}`)
    if (legacy) {
      localStorage.setItem(chatReadStorageKey(instanceId, readerId), legacy)
      return legacy
    }
    return null
  } catch {
    return null
  }
}

/** Drop local unread watermarks for this board (reader + legacy key). */
export function clearChatWatermark(
  instanceId: string | null | undefined,
  readerId?: string | null,
): void {
  if (!instanceId) return
  try {
    localStorage.removeItem(chatReadStorageKey(instanceId, readerId))
    localStorage.removeItem(`buxus.sc.chatRead:${instanceId}`)
  } catch {
    /* private mode */
  }
}

function wallIso(c: ScenarioComment): string {
  // Unread watermark is wall-clock only — never fall back to story `createdAt`
  // (story times are often earlier than a prior watermark and would never count).
  return (c.createdAtWallIso || '').trim()
}

function compareWall(a: string, b: string): number {
  if (a === b) return 0
  return a < b ? -1 : 1
}

/** Live room lines only (master YouTube thread). */
export function liveRoomChat(session: ScenarioSession): ScenarioComment[] {
  return (session.roomChat ?? []).filter((c) => {
    if (!c || c.removedAtWallIso) return false
    // roomChat is live-only; tolerate missing `source` after wire quirks.
    return !c.source || c.source === 'live'
  })
}

/** Accept array or JSON-string roomChat from SetSessionFields / Snapshot. */
export function coerceRoomChat(raw: unknown): ScenarioComment[] | null {
  let arr: unknown = raw
  if (typeof arr === 'string') {
    try {
      arr = JSON.parse(arr) as unknown
    } catch {
      return null
    }
  }
  if (arr && typeof arr === 'object' && !Array.isArray(arr)) {
    const wrapped = arr as { $values?: unknown; roomChat?: unknown; RoomChat?: unknown }
    arr = wrapped.$values ?? wrapped.roomChat ?? wrapped.RoomChat ?? arr
  }
  if (!Array.isArray(arr)) return null
  const out: ScenarioComment[] = []
  for (const row of arr) {
    if (!row || typeof row !== 'object') continue
    const c = row as ScenarioComment
    if (!c.id || typeof c.body !== 'string') continue
    out.push({
      ...c,
      source: c.source === 'script' || c.source === 'system' ? c.source : 'live',
      createdAt: typeof c.createdAt === 'string' ? c.createdAt : new Date().toISOString(),
      createdAtWallIso:
        typeof c.createdAtWallIso === 'string' && c.createdAtWallIso.trim()
          ? c.createdAtWallIso.trim()
          : new Date().toISOString(),
    })
  }
  return out
}

/**
 * One-time heal: flatten live lines from older per-run threads into roomChat.
 * Keeps existing roomChat; only runs when roomChat is empty.
 */
export function healRoomChatFromRuns(session: ScenarioSession): ScenarioSession {
  if ((session.roomChat ?? []).some((c) => c.source === 'live')) return session
  const byId = new Map<string, ScenarioComment>()
  for (const run of session.exerciseRuns ?? []) {
    for (const c of run.comments ?? []) {
      if (!c?.id || c.source !== 'live' || c.removedAtWallIso) continue
      if (byId.has(c.id)) continue
      byId.set(c.id, {
        ...structuredClone(c),
        runId: c.runId ?? c.originRunId ?? run.id,
        roomPhase: c.roomPhase ?? 'running',
      })
    }
  }
  if (!byId.size) return session
  const roomChat = [...byId.values()].sort((a, b) => compareWall(wallIso(a), wallIso(b)))
  return { ...session, roomChat }
}

/** @returns true when the watermark advanced. */
export function markChatRead(
  instanceId: string | null | undefined,
  roomLines: readonly ScenarioComment[],
  readerId?: string | null,
): boolean {
  if (!instanceId || !roomLines.length) return false
  let maxAt = wallIso(roomLines[0]!)
  for (const c of roomLines) {
    const w = wallIso(c)
    if (compareWall(w, maxAt) > 0) maxAt = w
  }
  if (!maxAt) return false
  const prev = readChatWatermark(instanceId, readerId)
  if (prev != null && compareWall(maxAt, prev) <= 0) return false
  try {
    localStorage.setItem(chatReadStorageKey(instanceId, readerId), maxAt)
    return true
  } catch {
    return false
  }
}

/** Unread = new live room lines since watermark (not playhead / scrub). */
export function countChatUnread(
  instanceId: string | null | undefined,
  roomLines: readonly ScenarioComment[],
  opts?: {
    readerId?: string | null
    ignoreAuthorIds?: readonly (string | null | undefined)[]
  },
): number {
  const ignore = new Set(
    (opts?.ignoreAuthorIds ?? []).map((id) => (id || '').trim()).filter(Boolean),
  )
  const candidates = ignore.size
    ? roomLines.filter((c) => !ignore.has((c.authorId || '').trim()))
    : roomLines
  const mark = readChatWatermark(instanceId, opts?.readerId)
  if (!mark) return candidates.length
  return candidates.filter((c) => {
    const w = wallIso(c)
    // No wall stamp → still unread (do not compare against story time).
    if (!w) return true
    return compareWall(w, mark) > 0
  }).length
}

export type ChatPaneMode = 'room' | 'liveRun' | 'runReview'

export type ChatPaneSections = {
  /**
   * Story-timed block, gated by playhead.
   * - liveRun / runReview: script + run-stamped live
   * - room (Previewing / Editing / …): script injects only (scrub with the story)
   */
  duringRun: ScenarioComment[]
  /**
   * Wall-order facilitation thread (Session).
   * - `room`: full live ledger (incl. run-stamped) so chat does not vanish after Stop.
   * - `liveRun`: always empty — take chat is only the during-run block.
   * - `runReview`: untagged lines only (between-takes / debrief); not scrubbed.
   */
  room: ScenarioComment[]
  layout: ChatPaneMode
  /** Version has authored script lines (Story lane may be empty at early playheads). */
  hasScript: boolean
}

function sortByStory(lines: readonly ScenarioComment[]): ScenarioComment[] {
  return [...lines].sort((a, b) => compareStory(a.createdAt, b.createdAt))
}

function sortByWall(lines: readonly ScenarioComment[]): ScenarioComment[] {
  return [...lines].sort((a, b) => compareWall(wallIso(a), wallIso(b)))
}

function liveLinesForRun(
  room: readonly ScenarioComment[],
  runId: string | null,
): ScenarioComment[] {
  if (runId) {
    return room.filter((c) => (c.runId ?? c.originRunId) === runId)
  }
  return room.filter((c) => c.roomPhase === 'running')
}

/** Untagged live lines (posted outside a take — between takes, Previewing, Reviewing). */
export function openRoomChatLines(session: ScenarioSession): ScenarioComment[] {
  return liveRoomChat(session).filter((c) => !(c.runId ?? c.originRunId)?.trim())
}

/**
 * Chat sections for the spine pane — never mixes scrub clocks in one list.
 * - room: Story (script at playhead) + Session (live by wall). Previewing / Editing / …
 * - liveRun: Story only (script + this take’s live at playhead). No Session block.
 * - runReview: Story (scrubbed) + Session (untagged, wall, sticky).
 */
export function chatSectionsForPane(
  session: ScenarioSession,
  opts: {
    mode: ChatPaneMode
    playheadIso: string
    runId?: string | null
  },
): ChatPaneSections {
  const room = liveRoomChat(session)
  const script = scriptCommentsForVersion(session, session.selectedVersionId)
  const hasScript = script.some((c) => !c.removedAtWallIso)

  if (opts.mode === 'room') {
    return {
      duringRun: sortByStory(
        commentsVisibleAt(
          script.filter((c) => !c.removedAtWallIso),
          opts.playheadIso,
        ),
      ),
      room: sortByWall(room),
      layout: 'room',
      hasScript,
    }
  }

  const runId = opts.runId?.trim() || null
  const runLive = liveLinesForRun(room, runId)
  const duringRun = sortByStory(
    commentsVisibleAt(
      [...script, ...runLive].filter((c) => !c.removedAtWallIso),
      opts.playheadIso,
    ),
  )

  if (opts.mode === 'liveRun') {
    return { duringRun, room: [], layout: 'liveRun', hasScript }
  }

  return {
    duringRun,
    room: sortByWall(room.filter((c) => !(c.runId ?? c.originRunId)?.trim())),
    layout: 'runReview',
    hasScript,
  }
}

/** @deprecated Prefer chatSectionsForPane — flat merge for older callers. */
export function chatLinesForPane(
  session: ScenarioSession,
  opts: {
    mode: ChatPaneMode
    playheadIso: string
    runId?: string | null
  },
): ScenarioComment[] {
  const s = chatSectionsForPane(session, opts)
  if (s.layout === 'runReview') {
    const byId = new Map<string, ScenarioComment>()
    for (const c of s.duringRun) byId.set(c.id, c)
    for (const c of s.room) byId.set(c.id, c)
    return [...byId.values()]
  }
  return s.layout === 'liveRun' ? s.duringRun : s.room
}

export function inferRoomChatPhase(opts: {
  lifecycle?: string | null
  liveTakeActive: boolean
  inRunReview: boolean
  boardActivity?: string | null
}): RoomChatPhase {
  if (opts.liveTakeActive) return 'running'
  if (opts.inRunReview) return 'reviewing'
  if (opts.boardActivity === 'editing') return 'editing'
  if (opts.lifecycle === 'planned') return 'planned'
  return 'preview'
}
